import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, FolderKanban, DollarSign, Layers, Monitor, LogOut, ChevronRight, Share2, Menu, X, ClipboardCheck, Inbox, BarChart3, TrendingUp } from 'lucide-react';
import useAuthStore from '@/stores/authStore';

const sidebarLinks = [
  { to: '/admin',          label: 'Dashboard',  icon: LayoutDashboard, end: true },
  { to: '/admin/analytics', label: 'Analytics',  icon: BarChart3 },
  { to: '/admin/growth',   label: 'Follower Growth', icon: TrendingUp },
  { to: '/admin/hero',     label: 'Hero',       icon: Monitor },
  { to: '/admin/users',    label: 'Users',      icon: Users },
  { to: '/admin/posts',    label: 'Blog Posts', icon: FileText },
  { to: '/admin/projects', label: 'Projects',   icon: FolderKanban },
  { to: '/admin/pricing',  label: 'Pricing',    icon: DollarSign },
  { to: '/admin/features', label: 'Features',   icon: Layers },
  { to: '/admin/social',   label: 'Social Posts', icon: Share2 },
  { to: '/admin/content-loop', label: 'Content Loop Review', icon: ClipboardCheck },
  { to: '/admin/leads', label: 'Consulting Leads', icon: Inbox },
];

export default function AdminLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeSidebar = () => setSidebarOpen(false);

  const SidebarContent = () => (
    <>
      {/* Brand */}
      <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
        <div>
          <span className="text-lg font-bold tracking-tight">DataLogicHub</span>
          <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">Admin</span>
        </div>
        {/* Close button — mobile only */}
        <button
          onClick={closeSidebar}
          className="md:hidden p-1 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      </div>

      {/* User info */}
      <div className="px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#14B8A6] flex items-center justify-center font-semibold text-sm shrink-0">
            {user?.displayName?.charAt(0)?.toUpperCase() ?? 'A'}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{user?.displayName ?? 'Admin'}</p>
            <p className="text-xs text-white/60 truncate">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {sidebarLinks.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={closeSidebar}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-white/20 text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`
            }
          >
            <Icon size={18} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Footer actions */}
      <div className="px-3 py-4 border-t border-white/10 space-y-1">
        <NavLink
          to="/"
          onClick={closeSidebar}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors"
        >
          <ChevronRight size={18} />
          Back to site
        </NavLink>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors"
        >
          <LogOut size={18} />
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-[#f0f4f8]">

      {/* ── Desktop sidebar (always visible on md+) ── */}
      <aside className="hidden md:flex w-64 bg-[#1E3A8A] text-white flex-col shrink-0">
        <SidebarContent />
      </aside>

      {/* ── Mobile sidebar (slide-in drawer) ── */}
      {/* Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}
      {/* Drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#1E3A8A] text-white flex flex-col shrink-0 transform transition-transform duration-300 ease-in-out md:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* ── Main content ── */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <header className="md:hidden flex items-center gap-3 px-4 py-3 bg-[#1E3A8A] text-white shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
          <span className="text-sm font-bold tracking-tight">DataLogicHub</span>
          <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Admin</span>
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

    </div>
  );
}
